import React, { FC, useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import {
    IkonAktivreminder,
    IkonFjernreminder,
    IkonPassivreminder,
} from '../../../../assets/icons'
import { colors } from '../../../../theme/palette'
import { L, L_HTML } from '../../../providers/Lokalise'
import { Button } from '../../../ui/Button/Button'
import { CircleIcon2 } from '../../../ui/Circle/Circle'
import { Typography } from '../../../ui/Typography/Typography'
import { Reminder } from '../../../../models/Case'
import { formatDate } from '../../../../utils/format'
import { CaseDetail } from '../../../../models/CaseDetail'
import RemoveReminder from './RemoveReminder'
import { Tooltip } from '../../../ui/Tooltip/Tooltip'

interface ReminderCardProps {
    reminder: Reminder
    currentCase: CaseDetail
    onUpdated?: () => void
}

const useStyles = createUseStyles({
    card: {
        position: 'relative',
        backgroundColor: colors.white,
        width: '100%',
        color: 'black',
        marginBottom: '10px',
        borderRadius: '4px',
        border: `1px solid ${colors.borderColor}`,
        padding: '1rem',
        transition: 'all 0.5s linear',
    },
    actions: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        gap: '10px',
    },
    date: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '20px',
    },
    deleteButtons: {
        display: 'flex',
        transition: 'all .3s ease-in',
        marginTop: '20px',
    },
    title: {
        display: '-webkit-box',
        WebkitLineClamp: '1',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    titleOpen: {
        display: '-webkit-box',
        // WebkitLineClamp: '1',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        // textOverflow: 'ellipsis',
    },
    content: {
        marginTop: '10px',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        whiteSpace: 'pre-wrap',
        textOverflow: 'ellipsis',
    },
    contentOpen: {
        marginTop: '10px',
        display: '-webkit-box',
        whiteSpace: 'pre-wrap',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
})

const ReminderCard: FC<ReminderCardProps> = ({
    reminder,
    currentCase,
    onUpdated,
}) => {
    const classes = useStyles()
    const [showDelete, setShowDelete] = useState(false)
    const [readMore, setReadMore] = useState(false)
    const [isMore, setIsMore] = useState(false)
    const contentRef = useRef<HTMLSpanElement>()
    const titleRef = useRef<HTMLSpanElement>()
    const [removeReminderOpen, setRemoveReminderOpen] = useState(false)

    const checkLines = (ref, lines: number): void => {
        // eslint-disable-next-line no-unused-expressions
        const lineHeight = window
            .getComputedStyle(ref.current)
            .getPropertyValue('line-height')

        // eslint-disable-next-line no-unused-expressions
        ref.current &&
            Math.round(ref.current.scrollHeight / parseFloat(lineHeight)) >
                lines &&
            setIsMore(true)
    }

    useEffect(() => {
        checkLines(contentRef, 2)
    }, [contentRef, reminder])

    useEffect(() => {
        checkLines(titleRef, 1)
    }, [titleRef, reminder])

    return (
        <div className={classes.card}>
            {/* --------- headline -------- */}
            <Typography
                size="S"
                weight="bold"
                style={{
                    paddingBottom: '.5rem',
                    paddingRight: '100px',
                    wordBreak: 'break-word',
                    display: 'flex',
                    gap: '10px',
                }}
            >
                {reminder?.status === 'Aktiv' ? (
                    <IkonAktivreminder width="22px" fill={colors.green} />
                ) : null}
                {reminder?.status === 'Passiv' ? (
                    <IkonPassivreminder width="12px" fill={colors.green} />
                ) : null}
                <span
                    ref={titleRef}
                    className={readMore ? classes.titleOpen : classes.title}
                >
                    {formatDate(new Date(reminder.date))}
                </span>
            </Typography>
            {/* ------- text content --------- */}
            <Typography size="XS">
                <span
                    ref={contentRef}
                    className={readMore ? classes.contentOpen : classes.content}
                    dangerouslySetInnerHTML={{ __html: reminder.content }}
                />
            </Typography>
            {/* ---------- date -------- */}
            <div className={classes.date}>
                {isMore && (
                    <Button
                        type="action"
                        style={{
                            textDecoration: 'underline',
                        }}
                        onClick={() => setReadMore((prev) => !prev)}
                    >
                        {readMore
                            ? L_HTML('NOTER_SIDEBAR_VIS_MINDRE')
                            : L_HTML('NOTER_SIDEBAR_VIS_MERE')}
                    </Button>
                )}
            </div>

            <Tooltip content={L('LØN_FJERN_REMINDER_DIALOG_FJERN_TOOLTIP')}>
                <div className={classes.actions}>
                    <Button
                        type="action"
                        onClick={() => setRemoveReminderOpen(true)}
                        disabled={
                            !currentCase.allowedUpdateActionList.includes(
                                'Sagsbehandle'
                            )
                        }
                    >
                        <CircleIcon2
                            width="34px"
                            backgroundColor={colors.red100}
                        >
                            <IkonFjernreminder
                                width="20px"
                                fill={colors.caseRed500}
                            />
                        </CircleIcon2>
                    </Button>
                </div>
            </Tooltip>

            {removeReminderOpen && (
                <RemoveReminder
                    caseRegistrationDate={currentCase.caseRegistrationTime}
                    caseId={currentCase.caseId}
                    reminderId={reminder.id}
                    onCancel={() => setRemoveReminderOpen(false)}
                    onSuccess={() => {
                        setRemoveReminderOpen(false)
                        onUpdated?.()
                    }}
                />
            )}
        </div>
    )
}

export { ReminderCard }
